import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Volume2, Palette, Megaphone, RotateCcw } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Switch } from "@/components/ui/switch";
import { announce, primeAudio, resetAudio } from "@/lib/announce";

export default function Settings() {
  const { user, updateSettings } = useAuth();
  const { theme } = useTheme();
  const [audio, setAudio] = useState(user?.settings?.audio_enabled ?? true);
  const [saving, setSaving] = useState(false);

  const save = async (patch) => {
    setSaving(true);
    try {
      await updateSettings(patch);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const onAudio = (val) => {
    setAudio(val);
    if (val) { primeAudio(); announce("Audio announcements enabled", true); }
    save({ audio_enabled: val });
  };

  const onTheme = (dark) => save({ theme: dark ? "dark" : "light" });

  const doReset = async () => {
    resetAudio();
    if (!audio) { setAudio(true); await save({ audio_enabled: true }); }
    setTimeout(() => announce("Audio announcements reset and working", true), 250);
    toast.success("Audio reset — you should hear a test announcement");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Settings" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 grain">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Account</span>
          <h1 className="font-display font-bold tracking-tight text-3xl mt-2">Settings</h1>
        </motion.div>

        <div className="mt-8 rounded-2xl glow-border divide-y" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <Row icon={Volume2} title="Audio announcements"
            desc="Play a chime and speak court assignments aloud during open-play.">
            <Switch data-testid="settings-audio-toggle" checked={audio} onCheckedChange={onAudio} disabled={saving} />
          </Row>
          <Row icon={Palette} title="Dark mode"
            desc="Cyber dark theme. Turn off for a light command center.">
            <Switch data-testid="settings-theme-toggle" checked={theme === "dark"} onCheckedChange={onTheme} disabled={saving} />
          </Row>
          <Row icon={Megaphone} title="Test announcement"
            desc="Hear how court call-outs will sound.">
            <button data-testid="settings-test-audio"
              onClick={() => { primeAudio(); announce("Now staging on Court 1", true); }}
              className="btn-glow rounded-full px-4 py-2 text-sm font-bold">Play</button>
          </Row>
          <Row icon={RotateCcw} title="Reset announcements"
            desc="Announcements silent or stuck? Reset the voice engine and re-enable audio.">
            <button data-testid="settings-reset-audio" onClick={doReset}
              className="btn-glow rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
          </Row>
        </div>

        <div className="mt-6 rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }}>
          <h4 className="font-display font-bold mb-3">Profile</h4>
          <div className="space-y-2 text-sm" style={{ color: "var(--text-2)" }}>
            <div className="flex justify-between"><span>Name</span><span className="font-semibold" style={{ color: "var(--text)" }}>{user?.name}</span></div>
            <div className="flex justify-between"><span>Email</span><span className="font-semibold mono" style={{ color: "var(--text)" }}>{user?.email}</span></div>
            <div className="flex justify-between"><span>Sign-in</span><span className="font-semibold uppercase mono text-primary-c">{user?.auth_provider}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4 p-6" style={{ borderColor: "var(--border)" }}>
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-10 h-10 rounded-xl glass flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-primary-c" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold">{title}</div>
          <div className="text-sm" style={{ color: "var(--text-2)" }}>{desc}</div>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
